import { Button, cn, Select } from "@tellescope/design-system";
import { Input } from "@tellescope/ui";
import { FLOATING_THUMB_CLASS, SCROLL_SURFACE_CLASS, scrollSurfaceStyle } from "@tellescope/react-hooks";
import { Search } from "lucide-react";
import { useTranslation } from "react-i18next";
import { RelatedContactsListItem } from "./RelatedContactsListItem";
import { useRelatedContactsAddView, type UseRelatedContactsAddViewArgs } from "./useRelatedContactsAddView";
import { useBlockContent } from "../BlockContent/useBlockContent";
import { BlockEmptyState } from "../BlockEmptyState";

export type RelatedContactsAddViewProps = UseRelatedContactsAddViewArgs & {
  contentClassName?: string;
};

export const RelatedContactsAddView = (props: RelatedContactsAddViewProps) => {
  const { t } = useTranslation("contacts");
  const { isOverflowing, scrollRef, thumbRef, handleScroll } = useBlockContent();
  const {
    relationshipTypes,
    addRelationship,
    setAddRelationship,
    addSearchQuery,
    setAddSearchQuery,
    addSelectedContactId,
    setAddSelectedContactId,
    filteredAddContacts,
    canAddContact,
    addRelatedContact,
    isLoading,
    isAdding,
  } = useRelatedContactsAddView(props);

  return (
    <div className="mt-2 flex flex-1 flex-col gap-4 min-h-0">
      <div className="flex flex-col gap-2">
        <Select value={addRelationship} onValueChange={setAddRelationship}>
          <Select.Trigger id="related-contact-relationship" className="w-full">
            <Select.Value placeholder={t("Select relationship type")} />
          </Select.Trigger>
          <Select.Content>
            {relationshipTypes.map((type) => (
              <Select.Item key={type} value={type}>
                {type}
              </Select.Item>
            ))}
          </Select.Content>
        </Select>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder={t("Search contacts...")}
          value={addSearchQuery}
          onChange={(e) => setAddSearchQuery(e.target.value)}
          className="pl-9"
        />
      </div>

      {isLoading ? (
        <div className="flex flex-1 items-center justify-center">
          <BlockEmptyState>{t("Loading contacts...")}</BlockEmptyState>
        </div>
      ) : filteredAddContacts.length === 0 ? (
        <div className="flex flex-1 items-center justify-center">
          <BlockEmptyState>
            {addSearchQuery ? t("No contacts found matching your search") : t("No contacts available")}
          </BlockEmptyState>
        </div>
      ) : (
        <div className="relative flex-1 min-h-0">
          <div
            ref={scrollRef}
            onScroll={handleScroll}
            style={scrollSurfaceStyle({ faded: isOverflowing })}
            className={cn(
              "h-full min-h-0 overflow-y-auto border rounded-md",
              SCROLL_SURFACE_CLASS,
              props.contentClassName
            )}
          >
            <div className="space-y-1 p-2">
              {filteredAddContacts.map((c) => {
                const isSelected = addSelectedContactId === c.id;
                return (
                  <RelatedContactsListItem
                    key={c.id}
                    contact={c}
                    selected={isSelected}
                    onClick={() => setAddSelectedContactId(isSelected ? "" : c.id)}
                  />
                );
              })}
            </div>
          </div>
          <div ref={thumbRef} className={FLOATING_THUMB_CLASS} />
        </div>
      )}

      <div className="flex justify-end gap-3">
        <Button variant="outline" onClick={props.closeAddView}>
          {t("Cancel")}
        </Button>
        <Button onClick={addRelatedContact} disabled={!canAddContact || isAdding}>
          {t("Add")}
        </Button>
      </div>
    </div>
  );
};
